import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useStableAuth } from '@/hooks/useStableAuth';
import { parseOFX, isValidOFX, OFXStatement, OFXTransaction } from '@/utils/ofxParser';

export interface OFXImportResult {
  total: number;
  imported: number;
  duplicates: number;
  errors: number;
}

export const useOFXImport = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId, userId } = useStableAuth();
  const [parsedStatement, setParsedStatement] = useState<OFXStatement | null>(null);

  // Parse OFX file
  const parseFile = async (file: File): Promise<OFXStatement> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          
          if (!isValidOFX(content)) {
            reject(new Error('Arquivo OFX inválido'));
            return;
          }
          
          const statement = parseOFX(content);
          setParsedStatement(statement);
          resolve(statement);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsText(file, 'ISO-8859-1'); // OFX usually uses ISO-8859-1
    });
  };

  // Import mutation usando UPSERT para idempotência
  const importMutation = useMutation({
    mutationFn: async ({
      statement,
      financialAccountId,
      bankIntegrationId,
      cutoffDate,
    }: {
      statement: OFXStatement;
      financialAccountId: string;
      bankIntegrationId?: string;
      cutoffDate?: string;
    }): Promise<OFXImportResult> => {
      if (!companyId) throw new Error('Company ID não encontrado');
      
      const result: OFXImportResult = {
        total: statement.transactions.length,
        imported: 0,
        duplicates: 0,
        errors: 0,
      };
      
      // Filtrar transações anteriores ao cutoff
      const filteredTransactions = cutoffDate
        ? statement.transactions.filter(t => t.date.toISOString().split('T')[0] >= cutoffDate)
        : statement.transactions;
      
      result.total = filteredTransactions.length;
      
      // Preparar todas as transações para upsert
      const toUpsert = filteredTransactions.map(t => ({
        company_id: companyId,
        financial_account_id: financialAccountId,
        bank_integration_id: bankIntegrationId || null,
        external_id: t.id,
        transaction_date: t.date.toISOString().split('T')[0],
        description: t.description.substring(0, 255),
        value: Math.abs(t.amount), // Garantir valor positivo
        transaction_type: t.amount >= 0 ? 'credit' : 'debit',
        category: mapTransactionType(t.type),
        reconciliation_status: 'pending',
        synced_at: new Date().toISOString(),
        // Campos detalhados do OFX
        counterpart_name: t.payeeName?.substring(0, 255) || null,
        counterpart_document: null, // OFX não contém CPF/CNPJ
        boleto_our_number: t.checkNumber || null,
      }));
      
      if (toUpsert.length > 0) {
        // Upsert em batches de 100 - ignorar duplicatas automaticamente
        for (let i = 0; i < toUpsert.length; i += 100) {
          const batch = toUpsert.slice(i, i + 100);
          
          // Usar upsert com ignoreDuplicates para idempotência
          const { data, error } = await supabase
            .from('bank_transactions')
            .upsert(batch, {
              onConflict: 'company_id,bank_integration_id,external_id',
              ignoreDuplicates: true, // Ignora duplicatas silenciosamente
            })
            .select('id');
          
          if (error) {
            console.error('Error upserting batch:', error);
            result.errors += batch.length;
          } else {
            // Contar quantos foram realmente inseridos vs duplicatas
            const insertedCount = data?.length || 0;
            result.imported += insertedCount;
            result.duplicates += batch.length - insertedCount;
          }
        }
      }
      
      return result;
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['financial-dashboard'] });

      // D-140C-ii.4: match por chave forte pós-import — best-effort, import
      // nunca falha por match.
      supabase.rpc('match_bank_transactions_by_key')
        .then(({ data, error }) => {
          if (error) {
            console.error('[match-engine] Falha pós-OFX:', error.message);
            return;
          }
          // Retorno tipado como Json — narrow local do shape do motor
          const r = (data ?? {}) as { linked_e2e?: number; linked_boleto?: number };
          const linked = (r.linked_e2e ?? 0) + (r.linked_boleto ?? 0);
          if (linked > 0) {
            queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
            toast({
              title: 'Conciliação automática',
              description: `${linked} lançamento(s) conciliado(s) automaticamente por chave forte.`,
            });
          }
        });

      toast({
        title: 'Importação concluída',
        description: `${result.imported} transações importadas, ${result.duplicates} duplicadas ignoradas.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro na importação',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    parseFile,
    parsedStatement,
    clearParsedStatement: () => setParsedStatement(null),
    importTransactions: importMutation.mutate,
    importTransactionsAsync: importMutation.mutateAsync,
    isImporting: importMutation.isPending,
    importResult: importMutation.data,
    importError: importMutation.error,
  };
};

// Map OFX transaction types to categories
function mapTransactionType(trnType: string): string {
  const typeMap: Record<string, string> = {
    CREDIT: 'credit',
    DEBIT: 'debit',
    INT: 'interest',
    DIV: 'dividend',
    FEE: 'fee',
    SRVCHG: 'service_charge',
    DEP: 'deposit',
    ATM: 'atm',
    POS: 'pos',
    XFER: 'transfer',
    CHECK: 'check',
    PAYMENT: 'payment',
    CASH: 'cash',
    DIRECTDEP: 'direct_deposit',
    DIRECTDEBIT: 'direct_debit',
    REPEATPMT: 'recurring_payment',
    OTHER: 'other',
  };
  
  return typeMap[trnType.toUpperCase()] || 'other';
}
